import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Music, User, Mail, Lock, Phone, ArrowLeft } from 'lucide-react';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AuthContext } from '@/App';
import { useLanguage } from '@/utils/translations';
import Footer from '@/components/Footer';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  
  // Registration mode and access control
  const [registrationMode, setRegistrationMode] = useState(null);
  const [accessBlocked, setAccessBlocked] = useState(false);
  
  // Get invitation token from URL (?group=TOKEN) OR from localStorage (set by InvitationPage)
  const urlToken = searchParams.get('group');
  const storedToken = localStorage.getItem('invitation_token');
  const invitationToken = urlToken || storedToken;
  
  // Check registration mode on mount
  useEffect(() => {
    const checkRegistrationMode = async () => {
      try {
        const response = await axios.get(`${API}/festivals/registration-mode`);
        const mode = response.data.registration_mode;
        setRegistrationMode(mode);
        
        // Block access if mode is closed OR if mode is group_only and no token
        // Check both URL param AND localStorage for token
        const hasToken = urlToken || localStorage.getItem('invitation_token');
        
        if (mode === 'closed') {
          setAccessBlocked(true);
        } else if (mode === 'group_only' && !hasToken) {
          setAccessBlocked(true);
        }
      } catch (error) {
        console.log('Could not fetch registration mode');
      }
    };
    checkRegistrationMode();
  }, [urlToken]);
  
  // Store invitation token in localStorage if present in URL (for OAuth flow)
  useEffect(() => {
    if (urlToken) {
      localStorage.setItem('invitation_token', urlToken);
      console.log('🎫 Stored invitation token for OAuth:', urlToken);
    }
  }, [urlToken]);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    birthdate: '',  // Mandatory birthdate for Danish regulatory compliance
    language: 'da',
    t_shirt_size: '',
    consent_gdpr: false  // Unified privacy policy consent (includes GDPR)
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  // Check for OAuth session on page load
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const sessionId = hashParams.get('session_id');
      
      if (sessionId) {
        setOauthLoading(true);
        try {
          // Check if there's a stored invitation token
          const storedInvitationToken = localStorage.getItem('invitation_token');
          
          const response = await axios.post(`${API}/auth/oauth/session-data`, {
            invitation_token: storedInvitationToken
          }, {
            headers: {
              'X-Session-ID': sessionId
            }
          });
          
          // Clear the stored invitation token after use
          if (storedInvitationToken) {
            localStorage.removeItem('invitation_token');
          }
          
          if (response.data.account_linking_required) {
            // Handle account linking
            handleAccountLinking(response.data);
          } else if (response.data.pending_approval) {
            // Account pending approval - redirect to login with message
            console.log('⏳ OAuth user account pending approval');
            // Clear URL hash and stored token
            window.history.replaceState({}, document.title, window.location.pathname);
            if (storedInvitationToken) {
              localStorage.removeItem('invitation_token');
            }
            navigate('/login?registered=true');
          } else {
            // Registration/Login successful - use JWT token for authentication
            login(response.data.token, response.data.user);
            
            // Clear the URL hash
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // Check for return URL (e.g., invitation page)
            const returnUrl = localStorage.getItem('returnUrl');
            if (returnUrl) {
              localStorage.removeItem('returnUrl');
              navigate(returnUrl);
              return;
            }
            
            // Check if user needs to complete profile (missing age)
            if (response.data.needs_profile_completion) {
              // Redirect to profile page to add age
              navigate('/profile');
            } else if (response.data.needs_terms_acceptance) {
              // Redirect to dashboard where they'll see the terms modal
              navigate('/dashboard');
            } else {
              // Profile complete, go to dashboard
              navigate('/dashboard');
            }
          }
        } catch (error) {
          console.error('OAuth registration failed:', error);
          setError('Google tilmelding fejlede. Prøv igen.');
        } finally {
          setOauthLoading(false);
        }
      }
    };
    
    handleOAuthCallback();
  }, [login, navigate]);

  const handleAccountLinking = async (linkingData) => {
    const confirmLink = window.confirm(
      `En konto med email ${linkingData.existing_email} findes allerede. Vil du linke den til din Google konto (${linkingData.google_name})?`
    );
    
    if (confirmLink) {
      try {
        const response = await axios.post(`${API}/auth/oauth/link-account`, {
          temp_session_data: linkingData.temp_session_data,
          confirm_linking: true
        });
        
        login(response.data.token, response.data.user);
        navigate('/dashboard');
      } catch (error) {
        setError('Konto sammenkædning fejlede. Prøv igen.');
      }
    }
  };

  const handleGoogleSignup = () => {
    // Redirect back to /register page where OAuth callback handler is
    const currentUrl = window.location.origin + '/register';
    const redirectUrl = encodeURIComponent(currentUrl);
    const oauthUrl = process.env.REACT_APP_OAUTH_URL || 'https://auth.emergentagent.com';
    window.location.href = `${oauthUrl}/?redirect=${redirectUrl}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate name fields are not empty
    if (!formData.first_name || !formData.first_name.trim()) {
      setError('Fornavn er påkrævet');
      return;
    }
    
    if (!formData.last_name || !formData.last_name.trim()) {
      setError('Efternavn er påkrævet');
      return;
    }

    if (!formData.consent_gdpr) {
      setError('Du skal acceptere privatlivspolitikken for at fortsætte');
      setLoading(false);
      return;
    }

    // Validate birthdate and calculate age
    if (!formData.birthdate) {
      setError('Fødselsdato er påkrævet.');
      setLoading(false);
      return;
    }
    
    const birthDate = new Date(formData.birthdate);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age < 15) {
      setError('Du skal være mindst 15 år gammel for at tilmelde dig.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    // Check for invitation token from multiple sources:
    // 1. URL query parameter (?group=TOKEN)
    // 2. localStorage (set by InvitationPage)
    // 3. Extract from returnUrl in localStorage
    let tokenForRegistration = searchParams.get('group'); // Check URL query param first
    
    if (!tokenForRegistration) {
      // Check localStorage directly (set by InvitationPage)
      tokenForRegistration = localStorage.getItem('invitation_token');
    }
    
    if (!tokenForRegistration) {
      // Fallback: check returnUrl in localStorage
      const returnUrl = localStorage.getItem('returnUrl');
      if (returnUrl && returnUrl.includes('/invite/')) {
        // Extract token from URL like "/invite/abc123"
        const parts = returnUrl.split('/invite/');
        if (parts.length > 1) {
          tokenForRegistration = parts[1];
        }
      }
    }

    console.log('🔍 Registration with invitation token:', tokenForRegistration);

    // Include invitation token in registration data if present
    const registrationData = {
      ...formData,
      email: formData.email.trim().toLowerCase(), // Normalize email to lowercase
      ...(tokenForRegistration && { invitation_token: tokenForRegistration })
    };

    try {
      // Step 1: Register user (this is the critical part)
      const response = await axios.post(`${API}/auth/register`, registrationData);
      
      // If we get here, registration succeeded!
      console.log('✅ Registration successful');
      
      // Check user's approval status from response
      const userApprovalStatus = response.data.user?.approval_status;
      
      if (userApprovalStatus === 'pending') {
        // User needs approval - redirect to login with pending message
        console.log('⏳ User account pending approval, redirecting to login');
        navigate('/login?registered=true');
        return;
      }
      
      // Step 2: If approved (rare case - e.g., admin creating user), allow login
      try {
        login(response.data.token, response.data.user);
        
        // Step 3: Navigation (non-critical)
        try {
          // Clean up returnUrl
          const returnUrl = localStorage.getItem('returnUrl');
          if (returnUrl) {
            localStorage.removeItem('returnUrl');
            if (tokenForRegistration) {
              // If registered via invitation, redirect to the invitation page to complete the flow
              navigate(returnUrl);
              setLoading(false);
              return;
            }
          }
          
          // Default redirect
          navigate('/dashboard');
        } catch (navError) {
          console.warn('Navigation after registration failed, but registration succeeded:', navError);
          // Registration succeeded, just redirect to login
          navigate('/login');
        }
      } catch (loginError) {
        console.warn('Auto-login after registration failed, but registration succeeded:', loginError);
        // Registration succeeded, redirect to login page with success message
        navigate('/login?registered=true');
      }
    } catch (err) {
      // Only show error if the actual registration API call failed
      console.error('Registration API call failed:', err);
      setError(err.response?.data?.detail || 'Registrering mislykkedes. Prøv venligst igen.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  // Show blocked access page if registration is not allowed
  if (accessBlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-200 py-12 px-4">
        <div className="w-full max-w-md mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-6 text-neutral-600 hover:text-neutral-900"
            onClick={() => navigate('/login')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tilbage til login
          </Button>

          <div className="bg-white rounded-2xl shadow-2xl p-8 animate-fadeIn">
            {/* Logo */}
            <div className="flex items-center justify-center mb-8">
              <Logo size="large" />
            </div>

            <div className="text-center">
              {registrationMode === 'closed' ? (
                <>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                    Tilmelding er lukket
                  </h2>
                  <p className="text-neutral-600 mb-6">
                    Tilmelding til frivilligkorpset er lukket i øjeblikket.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                    Tilmelding kræver gruppelink
                  </h2>
                  <p className="text-neutral-600 mb-6">
                    For at tilmelde dig som frivillig skal du bruge et link fra din forening.
                  </p>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-amber-800">
                      <strong>Har du et gruppelink?</strong>
                      <br />
                      Klik på linket fra din forening for at komme til tilmeldingssiden.
                    </p>
                  </div>
                </>
              )}
              
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
                <p className="text-sm text-neutral-700 mb-2">
                  <strong>Har du spørgsmål?</strong>
                </p>
                <p className="text-sm text-neutral-600">
                  Kontakt os på{' '}
                  <a 
                    href="mailto:frivillig@rub.dk" 
                    className="text-[#f4911c] hover:underline font-medium"
                  >
                    frivillig@rub.dk
                  </a>
                </p>
              </div>

              <Button
                className="mt-6 w-full bg-[#f4911c] hover:bg-[#d67d15]"
                onClick={() => navigate('/login')}
              >
                Gå til login
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-200 py-12 px-4">
      <div className="w-full max-w-2xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-6 text-neutral-600 hover:text-neutral-900"
          onClick={() => navigate('/')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.backToHome')}
        </Button>

        {/* Register Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 animate-fadeIn">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <Logo size="large" />
          </div>

          <h2 className="text-3xl font-bold text-center text-neutral-900 mb-2">{t('register.title')}</h2>
          <p className="text-center text-neutral-600 mb-8">{t('register.subtitle')}</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="error-message">
              {error}
            </div>
          )}

          {/* Google OAuth Section - Primary Option */}
          <div className="mb-6">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-neutral-700 border-neutral-300 hover:bg-neutral-50 flex items-center justify-center space-x-3"
              onClick={handleGoogleSignup}
              disabled={oauthLoading}
            >
              {oauthLoading ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Tilmelder...
                </div>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="font-medium">Tilmeld med Google</span>
                </>
              )}
            </Button>
            
            <div className="mt-4 relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-neutral-500">eller udfyld formularen</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="first_name" className="text-neutral-700 font-medium mb-2 block">
                  {t('register.firstName')} *
                </Label>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="h-12"
                  required
                  data-testid="first-name-input"
                />
              </div>

              <div>
                <Label htmlFor="last_name" className="text-neutral-700 font-medium mb-2 block">
                  {t('register.lastName')} *
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="h-12"
                  required
                  data-testid="last-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-neutral-700 font-medium mb-2 block">
                {t('auth.email')} *
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 h-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-neutral-700 font-medium mb-2 block">
                {t('auth.password')} *
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder={t('auth.password')}
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 h-12"
                  required
                  minLength={8}
                  data-testid="password-input"
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                Mindst 8 tegn med både bogstaver og tal
              </p>
            </div>

            <div>
              <Label htmlFor="phone" className="text-neutral-700 font-medium mb-2 block">
                {t('register.phone')}
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+45 12 34 56 78"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 h-12"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="birthdate" className="text-neutral-700 font-medium mb-2 block">
                Fødselsdato <span className="text-red-500">*</span>
              </Label>
              <Input
                id="birthdate"
                name="birthdate"
                type="date"
                max={new Date().toISOString().split('T')[0]}
                placeholder="ÅÅÅÅ-MM-DD"
                value={formData.birthdate}
                onChange={handleChange}
                className="h-12"
                required
                data-testid="birthdate-input"
              />
              <p className="text-xs text-neutral-500 mt-1">Påkrævet af danske myndighedsregler. GDPR beskyttet.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="t_shirt_size" className="text-neutral-700 font-medium mb-2 block">
                  {t('register.tshirtSize')}
                </Label>
                <Select 
                  name="t_shirt_size" 
                  value={formData.t_shirt_size}
                  onValueChange={(value) => setFormData({...formData, t_shirt_size: value})}
                >
                  <SelectTrigger className="h-12" data-testid="tshirt-select">
                    <SelectValue placeholder={t('register.selectSize')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="XS">XS</SelectItem>
                    <SelectItem value="S">S</SelectItem>
                    <SelectItem value="M">M</SelectItem>
                    <SelectItem value="L">L</SelectItem>
                    <SelectItem value="XL">XL</SelectItem>
                    <SelectItem value="XXL">XXL</SelectItem>
                    <SelectItem value="3XL">3XL</SelectItem>
                    <SelectItem value="4XL">4XL</SelectItem>
                    <SelectItem value="5XL">5XL</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="language" className="text-neutral-700 font-medium mb-2 block">
                  {t('register.language')}
                </Label>
                <Select 
                  name="language" 
                  value={formData.language}
                  onValueChange={(value) => setFormData({...formData, language: value})}
                >
                  <SelectTrigger className="h-12" data-testid="language-select">
                    <SelectValue placeholder={t('register.language')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="da">{t('register.danish')}</SelectItem>
                    <SelectItem value="en">{t('register.english')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4 border-t pt-6">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="consent_gdpr"
                  name="consent_gdpr"
                  checked={formData.consent_gdpr}
                  onCheckedChange={(checked) => setFormData({...formData, consent_gdpr: checked})}
                  data-testid="gdpr-checkbox"
                  required
                />
                <Label htmlFor="consent_gdpr" className="text-sm text-neutral-700 cursor-pointer">
                  Jeg accepterer <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#f4911c] hover:underline">privatlivspolitikken</a> (inkl. GDPR) *
                </Label>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#f4911c] hover:bg-[#C41A20] text-white h-12 text-lg font-semibold"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {t('register.creating')}
                </div>
              ) : (
                t('register.createAccountButton')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-neutral-600">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-[#f4911c] hover:text-[#C41A20] font-semibold" data-testid="login-link">
                {t('auth.signInLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RegisterPage;